/**
 * Player Matchup Component
 */

import React, { ReactElement } from 'react';
import { PlayerMatchupFormats, PlayerMatchupProps, PlayerMatchupTypes } from './playerMatchup.types';
import {
  PlayerMatchupWrapper,
  PlayerNameWrapper,
  PlayerDataWrapper,
  PlayerDecisionWrapper,
  PlayerStatsWrapper,
  PlayerInfoWrapper,
  PlayerDecisionAltWrapper,
  PlayerPregameWrapper,
  PlayerMatchupHeadshotWrapper,
} from './playerMatchup.style';
import Headshot from '../Headshot/headshot';
import { HeadshotTypes } from '../Headshot/headshot.types';
import { useTheme } from 'styled-components';
import Skeleton from '../Skeleton/skeleton';
import Link from '../Link/link';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const generatePlayerInfo = (props: any) => {
  const {
    type,
    jerseyNumber,
    pitchHand,
    decision,
    name,
    noNameLabel = 'TBD',
    simpleInline,
    playerStatsInline = false,
    alignment = 'left',
    customPlayerInfo,
    battingOrder,
    formatInfo,
    prefix,
  } = props;
  // if no name use default
  if (!name) {
    return (
      <PlayerNameWrapper {...formatInfo} simpleInline={simpleInline}>
        {noNameLabel}
      </PlayerNameWrapper>
    );
  }

  switch (type) {
    case PlayerMatchupTypes.PITCHERPREGAME:
      return (
        <React.Fragment>
          <PlayerNameWrapper simpleInline={simpleInline} {...formatInfo} className={'trk-playermatchup-name'}>
            {name}
          </PlayerNameWrapper>
          {!simpleInline && (
            <div>
              {pitchHand && (
                <PlayerPregameWrapper alignment={alignment} {...formatInfo}>
                  {pitchHand}HP
                </PlayerPregameWrapper>
              )}
              {jerseyNumber && (
                <PlayerPregameWrapper alignment={alignment} {...formatInfo}>
                  #{jerseyNumber}
                </PlayerPregameWrapper>
              )}
              {customPlayerInfo && (
                <PlayerPregameWrapper alignment={alignment} {...formatInfo}>
                  {customPlayerInfo}
                </PlayerPregameWrapper>
              )}
            </div>
          )}
        </React.Fragment>
      );
    case PlayerMatchupTypes.PITCHERLIVE:
    case PlayerMatchupTypes.HITTERLIVE:
      return (
        <React.Fragment>
          <PlayerNameWrapper className={'trk-playermatchup-name'} simpleInline={simpleInline} {...formatInfo}>
            {prefix}
            {battingOrder ? `${battingOrder}. ` : ''}
            {name}
          </PlayerNameWrapper>
          {customPlayerInfo && (
            <PlayerPregameWrapper alignment={alignment} {...formatInfo}>
              {customPlayerInfo}
            </PlayerPregameWrapper>
          )}
        </React.Fragment>
      );
    case PlayerMatchupTypes.PITCHERPOSTGAME: {
      const { useDecisionBox } = formatInfo;
      const DecisionWrapper = useDecisionBox ? PlayerDecisionAltWrapper : PlayerDecisionWrapper;
      return (
        <React.Fragment>
          <DecisionWrapper playerStatsInline={playerStatsInline} {...formatInfo}>
            {decision}
            {!playerStatsInline && !useDecisionBox ? ':' : ''}
          </DecisionWrapper>
          <PlayerNameWrapper
            simpleInline={simpleInline}
            {...formatInfo}
            type={type}
            className={'trk-playermatchup-name'}
          >
            {name}
          </PlayerNameWrapper>
        </React.Fragment>
      );
    }
    default:
      return;
  }
};

const PlayerMatchup = (props: PlayerMatchupProps) => {
  const {
    alignment = 'left',
    backgroundColor,
    className,
    format = PlayerMatchupFormats.ORIGINAL,
    headshotRatio,
    headshotType,
    headshotWidth = '35px',
    headshotZoomScale,
    isLoading,
    linkProps,
    name,
    playerId,
    playerStats = '-',
    playerStatsInline = false,
    showHeadShot = true,
    simpleInline,
    stacked = false,
    type,
    useDecisionBox = false,
  } = props;

  const { dark = false } = useTheme();

  const { VISUAL, LIST } = PlayerMatchupFormats;
  const isListFormat = format === LIST;
  const isVisualFormat = format === VISUAL;
  const formatInfo = { isListFormat, isVisualFormat, dark, useDecisionBox };

  const playerInfoContent = isLoading ? (
    <Skeleton height={12} width={35} margin={2} />
  ) : (
    generatePlayerInfo({ ...props, formatInfo })
  );

  const playerStatsContent = isLoading ? <Skeleton height={9} width={50} margin={0} /> : playerStats;

  const Matchup: ReactElement = (
    <PlayerMatchupWrapper
      alignment={alignment}
      linkProps={linkProps}
      name={name}
      className={className}
      stacked={stacked}
      {...formatInfo}
    >
      {!simpleInline && (
        <PlayerMatchupHeadshotWrapper>
          {showHeadShot && (
            <Headshot
              backgroundColor={headshotType !== HeadshotTypes.SPOTS ? backgroundColor : undefined}
              fixAspect
              isLoading={isLoading}
              playerId={playerId}
              ratio={headshotRatio}
              type={headshotType}
              width={headshotWidth}
              zoomScale={headshotZoomScale}
              alt={name}
            />
          )}
        </PlayerMatchupHeadshotWrapper>
      )}
      <PlayerDataWrapper
        alignment={alignment}
        simpleInline={simpleInline}
        stacked={stacked}
        playerStatsInline={playerStatsInline}
        aria-hidden="true"
        {...formatInfo}
      >
        <PlayerInfoWrapper alignment={alignment} type={type} {...formatInfo}>
          {playerInfoContent}
        </PlayerInfoWrapper>
        <PlayerStatsWrapper simpleInline={simpleInline} playerStatsInline={playerStatsInline} {...formatInfo}>
          {playerStatsContent}
        </PlayerStatsWrapper>
      </PlayerDataWrapper>
    </PlayerMatchupWrapper>
  );

  // Wrap a link if it exists
  // if no name then do not use a link as player is unknown
  if (linkProps && name) {
    return <Link {...linkProps}>{Matchup}</Link>;
  } else {
    return Matchup;
  }
};

export default PlayerMatchup;
